import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CameraCapture from '../CameraCapture';
import {
  useDeleteReferenceMutation,
  useUploadReferenceMutation,
} from '../../core/queries/mutations';
import ReferencePickerFields from './ReferencePickerFields';

function uploadErrorMessage(error, fallback) {
  return error?.response?.data?.error || error?.response?.data?.message || fallback;
}

export default function ReferenceUploadFlow({
  title,
  hint,
  references = [],
  requiredCount = 1,
  disabled = false,
  onReferencesChange,
  onUploadingChange,
  onError,
  cameraLabel,
  galleryLabel,
  removeLabel,
  testIdPrefix = 'photo',
  galleryInputTestId,
  pairTestId,
  showHeader = true,
  activeSlot: controlledActiveSlot,
  onActiveSlotChange,
}) {
  const { t } = useTranslation();
  const uploadReferenceMutation = useUploadReferenceMutation();
  const deleteReferenceMutation = useDeleteReferenceMutation();
  const [internalActiveSlot, setInternalActiveSlot] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState(0);
  const [showCamera, setShowCamera] = useState(false);
  const suppressSlotOpenUntilRef = useRef(0);
  const activeSlotIsControlled = controlledActiveSlot !== undefined;
  const activeSlot = controlledActiveSlot === undefined
    ? internalActiveSlot
    : controlledActiveSlot;
  const setActiveSlot = useCallback((nextSlot) => {
    if (!activeSlotIsControlled) setInternalActiveSlot(nextSlot);
    onActiveSlotChange?.(nextSlot);
  }, [activeSlotIsControlled, onActiveSlotChange]);
  const handleActiveSlotChange = useCallback((nextSlot) => {
    if (nextSlot !== null && Date.now() < suppressSlotOpenUntilRef.current) return;
    setActiveSlot(nextSlot);
  }, [setActiveSlot]);

  const updateReferences = useCallback((updater) => {
    onReferencesChange?.(updater);
  }, [onReferencesChange]);

  useEffect(() => {
    onUploadingChange?.(uploading, uploadingSlot);
  }, [onUploadingChange, uploading, uploadingSlot]);

  useEffect(() => {
    setActiveSlot(null);
    setSelectedSource(null);
    setShowCamera(false);
  }, [requiredCount, setActiveSlot]);

  const uploadFile = useCallback(async (file, slot = 0, source = 'gallery') => {
    if (!file || uploading || disabled) return false;

    const normalizedSlot = Math.max(0, Math.min(requiredCount - 1, Number(slot) || 0));
    setUploading(true);
    setUploadingSlot(normalizedSlot);
    setSelectedSource(source);
    onError?.(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await uploadReferenceMutation.mutateAsync(formData);
      if (!response?.url) {
        throw new Error('Reference upload did not return a URL');
      }

      const uploaded = {
        id: `${Date.now()}-${Math.random()}`,
        preview: response.url,
        url: response.url,
        source,
        owned: true,
      };

      const previous = references[normalizedSlot];
      if (previous?.owned !== false && previous?.url && previous.url !== uploaded.url) {
        deleteReferenceMutation.mutate(previous.url);
      }
      if (previous?.preview?.startsWith?.('blob:')) {
        URL.revokeObjectURL(previous.preview);
      }

      updateReferences((current = []) => {
        const next = [...current];
        next[normalizedSlot] = uploaded;
        return next.slice(0, requiredCount);
      });
      setActiveSlot(null);
      return true;
    } catch (error) {
      onError?.(uploadErrorMessage(
        error,
        t('prompt.uploadError', 'Failed to upload image'),
      ));
      return false;
    } finally {
      setUploading(false);
    }
  }, [
    deleteReferenceMutation,
    disabled,
    onError,
    references,
    requiredCount,
    t,
    updateReferences,
    uploadReferenceMutation,
    uploading,
  ]);

  const handleSourceSelect = useCallback((source, slot = 0) => {
    const normalizedSlot = Math.max(0, Math.min(requiredCount - 1, Number(slot) || 0));
    setSelectedSource(source);
    setUploadingSlot(normalizedSlot);
    if (source === 'camera') {
      setShowCamera(true);
    }
  }, [requiredCount]);

  const handleGalleryFileSelect = useCallback(
    (file, slot = 0) => uploadFile(file, slot, 'gallery'),
    [uploadFile],
  );

  const handleCameraCapture = useCallback(async (file) => {
    setShowCamera(false);
    await uploadFile(file, uploadingSlot, 'camera');
  }, [uploadFile, uploadingSlot]);

  const handleRemove = useCallback((slot = 0) => {
    suppressSlotOpenUntilRef.current = Date.now() + 350;
    const reference = references[slot];
    if (reference?.owned !== false && reference?.url) {
      deleteReferenceMutation.mutate(reference.url);
    }
    if (reference?.preview?.startsWith?.('blob:')) {
      URL.revokeObjectURL(reference.preview);
    }

    updateReferences((current = []) => {
      const next = [...current];
      delete next[slot];
      return next;
    });
    setActiveSlot(null);
    onError?.(null);
  }, [deleteReferenceMutation, onError, references, updateReferences]);

  return (
    <>
      <ReferencePickerFields
        title={title}
        hint={hint}
        references={references}
        requiredCount={requiredCount}
        uploading={uploading}
        uploadingSlot={uploadingSlot}
        busy={disabled || uploading}
        selectedSource={selectedSource}
        activeSlot={activeSlot}
        onActiveSlotChange={handleActiveSlotChange}
        onSourceSelect={handleSourceSelect}
        onGalleryFileSelect={handleGalleryFileSelect}
        onRemove={handleRemove}
        cameraLabel={cameraLabel}
        galleryLabel={galleryLabel}
        removeLabel={removeLabel}
        testIdPrefix={testIdPrefix}
        galleryInputTestId={galleryInputTestId}
        pairTestId={pairTestId}
        showHeader={showHeader}
      />

      {showCamera ? (
        <CameraCapture
          onCapture={handleCameraCapture}
          onClose={() => setShowCamera(false)}
        />
      ) : null}
    </>
  );
}
